// src/modules/Meetings/pages/MeetingViewPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Typography,
  Button,
  Space,
  Tag,
  Tooltip,
  message,
  Empty,
  Table,
  Input,
  Row,
  Col,
  Avatar,
  Divider,
} from "antd";
import {
  CalendarOutlined,
  ReloadOutlined,
  SearchOutlined,
  PlusOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { fetchMeetings } from "../../../api/meetingApi";

const { Title, Text } = Typography;

export default function MeetingViewPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const loadMeetings = async () => {
    setLoading(true);
    try {
      const data = await fetchMeetings(user?.id);
      setMeetings(Array.isArray(data) ? data : []);
    } catch (error) {
      message.error("Failed to load meetings");
      console.error("Error loading meetings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeetings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const getParticipantNames = (record) => {
    if (Array.isArray(record?.participant_names_arr) && record.participant_names_arr.length) {
      return record.participant_names_arr.filter(Boolean);
    }
    const fromString = String(record?.participant_names || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (fromString.length) return fromString;

    const ids = String(record?.user_ids || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (ids.length) return ids.map((id) => `User #${id}`);

    return [];
  };

  // Status mapping:
  // - "completed": meetingDate < now - 1 hour
  // - "ongoing":  now - 1 hour <= meetingDate < now
  // - "today":    now <= meetingDate < now + 1 day
  // - "upcoming": meetingDate >= now + 1 day
  const getMeetingStatus = (date) => {
    if (!date) return "unknown";
    const meetingDate = dayjs(date);
    const now = dayjs();

    if (meetingDate.isBefore(now.subtract(1, "hour"))) return "completed";
    if (meetingDate.isBefore(now)) return "ongoing";
    if (meetingDate.isBefore(now.add(1, "day"))) return "today";
    return "upcoming";
  };

  const filteredMeetings = useMemo(() => {
    let result = meetings;

    // Apply status filter
    if (activeTab !== "all") {
      result = result.filter((m) => {
        const status = getMeetingStatus(m.meeting_date_time);
        if (activeTab === "upcoming") return status === "upcoming";
        if (activeTab === "today") return status === "today" || status === "ongoing";
        if (activeTab === "past") return status === "completed";
        return true;
      });
    }

    // Apply search filter
    if (search.trim()) {
      const query = search.toLowerCase();
      result = result.filter((meeting) => {
        return (
          String(meeting.subject || "").toLowerCase().includes(query) ||
          String(meeting.link || "").toLowerCase().includes(query) ||
          String(meeting.user_ids || "").toLowerCase().includes(query) ||
          String(meeting.participant_names || "").toLowerCase().includes(query)
        );
      });
    }

    return result;
  }, [meetings, search, activeTab]);

  const statusConfig = {
    upcoming: { color: "blue", icon: <ClockCircleOutlined />, text: "Upcoming" },
    today: { color: "green", icon: <CheckCircleOutlined />, text: "Today" },
    ongoing: { color: "orange", icon: <ExclamationCircleOutlined />, text: "Ongoing" },
    completed: { color: "default", icon: null, text: "Completed" },
    unknown: { color: "gray", icon: null, text: "Unknown" },
  };


const renderJoinButton = (record) => {
  const status = getMeetingStatus(record.meeting_date_time);
  const link = (record?.link || "").trim();

  // Disable when no link or meeting already ended/unknown
  const isDisabled =
    !link || status === "completed" || status === "unknown";

  // Button styling + tooltip based on status
  let buttonProps = { type: "default" };
  let tip = "Join meeting";

  if (status === "ongoing" || status === "today") {
    buttonProps = {
      type: "primary",
      style: { backgroundColor: "#52c41a", borderColor: "#52c41a" },
    };
    tip = status === "ongoing" ? "Ongoing" : "Today";
  } else if (status === "upcoming") {
    buttonProps = { type: "default" };
    tip = "Upcoming";
  } else {
    // completed / unknown / any other
    tip = status === "completed" ? "Meeting ended" : "Unavailable";
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <Tooltip title={tip}>
        <Button
          {...buttonProps}
          icon={<LinkOutlined />}
          disabled={isDisabled}
          onClick={() =>
            !isDisabled &&
            window.open(link, "_blank", "noopener,noreferrer")
          }
        >
          Join
        </Button>
      </Tooltip>

      {/* Always show the link (so it can be copied) */}
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          title={link}
          style={{
            fontSize: 12,
            color: "#1677ff",
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "inline-block",
          }}
        >
         
        </a>
      ) : (
        <span style={{ fontSize: 12, color: "#999" }}>No link</span>
      )}
    </div>
  );
};

  const columns = [
    {
      title: "Meeting",
      dataIndex: "subject",
      key: "subject",
      render: (text, record) => {
        const status = getMeetingStatus(record.meeting_date_time);
        const cfg = statusConfig[status] || statusConfig.unknown;
        return (
          <Space size="middle">
            <Avatar size="large">{text?.[0]?.toUpperCase() || "M"}</Avatar>
            <div>
              <Text strong style={{ display: "block" }}>{text || "No Subject"}</Text>
              <Tag color={cfg.color} icon={cfg.icon} style={{ marginTop: 4 }}>
                {cfg.text}
              </Tag>
            </div>
          </Space>
        );
      },
      sorter: (a, b) => (a.subject || "").localeCompare(b.subject || ""),
    },
    {
      title: "Date & Time",
      dataIndex: "meeting_date_time",
      key: "meeting_date_time",
      render: (date) => (
        <Space direction="vertical" size={0}>
          <Text strong>{dayjs(date).format("MMM D, YYYY")}</Text>
          <Text type="secondary">{dayjs(date).format("h:mm A")}</Text>
        </Space>
      ),
      sorter: (a, b) => new Date(a.meeting_date_time) - new Date(b.meeting_date_time),
      defaultSortOrder: "descend",
    },
    {
      title: "Participants",
      dataIndex: "user_ids",
      key: "participants",
      render: (_, record) => {
        const names = getParticipantNames(record);
        return (
          <Avatar.Group maxCount={5} size="large" maxStyle={{ color: "#fff", backgroundColor: "#1890ff" }}>
            {names.map((name, idx) => (
              <Tooltip title={name} key={`${name}-${idx}`}>
                <Avatar>{name[0]?.toUpperCase()}</Avatar>
              </Tooltip>
            ))}
          </Avatar.Group>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => renderJoinButton(record),
      width: 140,
      align: "right",
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      <Card
        bordered={false}
        style={{ boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)", borderRadius: 12 }}
        bodyStyle={{ padding: 0 }}
      >
        <div style={{ padding: 24 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0 }}>
                <Space>
                  <CalendarOutlined style={{ color: "#1890ff" }} />
                  <span>Meetings</span>
                </Space>
              </Title>
            </Col>
            <Col>
              <Space>
                <Input
                  placeholder="Search meetings..."
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 280 }}
                  allowClear
                />
                <Button icon={<ReloadOutlined />} onClick={loadMeetings} loading={loading} />
              </Space>
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0" }} />

          <Space style={{ marginBottom: 16 }}>
            <Button type={activeTab === "all" ? "primary" : "default"} onClick={() => setActiveTab("all")}>
              All Meetings
            </Button>
            <Button
              type={activeTab === "upcoming" ? "primary" : "default"}
              onClick={() => setActiveTab("upcoming")}
            >
              Upcoming
            </Button>
            <Button type={activeTab === "today" ? "primary" : "default"} onClick={() => setActiveTab("today")}>
              Today
            </Button>
            <Button type={activeTab === "past" ? "primary" : "default"} onClick={() => setActiveTab("past")}>
              Past
            </Button>
          </Space>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredMeetings}
          loading={loading}
          scroll={{ x: true }}
          pagination={{
            position: ["bottomRight"],
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total) => (
              <Text strong style={{ marginRight: 16 }}>
                Showing {filteredMeetings.length} of {total} meetings
              </Text>
            ),
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<Text type="secondary">{user?.id ? "No meetings found" : "Please log in to view meetings"}</Text>}
              />
            ),
          }}
        />
      </Card>
    </div>
  );
}
